/**
 * Core i18n engine — framework-agnostic, works in both Node.js and browser.
 *
 * Design:
 * - English is always bundled (zero-cost default)
 * - Non-default locales are lazy-loaded via dynamic import()
 * - Listeners are notified on locale change for reactive updates
 * - Interpolation: {param} placeholders replaced by params object
 */

import en from './locales/en'
import type { Locale, Translations, TranslationParams, LocaleChangeListener } from './types'
import type { TranslationKey } from './locales/en'

let currentLocale: Locale = 'en'
let currentTranslations: Translations = en
const listeners = new Set<LocaleChangeListener>()

/**
 * Translate a key to the current locale string.
 * Falls back to English if key is missing in the current locale.
 *
 * Supports interpolation: t('overlay.revealInFinder', { fileManager: 'Finder' })
 * → "Reveal in Finder"
 */
export function t(key: TranslationKey, params?: TranslationParams): string {
  const count = params?.count
  const pluralKey = count === 1 ? `${key}_one` : `${key}_other`
  const resolvedKey = count !== undefined && pluralKey in en
    ? pluralKey as TranslationKey
    : key
  let value = currentTranslations[resolvedKey] ?? en[resolvedKey]
  if (params) {
    for (const [param, replacement] of Object.entries(params)) {
      value = value.replaceAll(`{${param}}`, String(replacement))
    }
  }
  return value
}

/** Get the current active locale */
export function getLocale(): Locale {
  return currentLocale
}

/**
 * Set the active locale. Lazy-loads the translation file if not English.
 * Notifies all listeners after the locale is changed.
 */
export async function setLocale(locale: Locale): Promise<void> {
  if (locale === currentLocale) return

  if (locale === 'en') {
    currentTranslations = en
  } else {
    const module = await loadLocale(locale)
    currentTranslations = module
  }

  currentLocale = locale
  for (const listener of listeners) {
    listener(locale)
  }
}

/**
 * Set locale synchronously (for cases where translations are pre-loaded).
 * Only works for 'en' or previously loaded locales.
 */
export function setLocaleSync(locale: Locale): void {
  if (locale === currentLocale) return

  if (locale === 'en') {
    currentTranslations = en
    currentLocale = locale
    for (const listener of listeners) {
      listener(locale)
    }
  } else {
    // For non-English, must use async setLocale
    void setLocale(locale)
  }
}

/**
 * Register a listener for locale changes.
 * Returns an unsubscribe function.
 */
export function onLocaleChange(fn: LocaleChangeListener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Get the current translations dictionary (for advanced use cases) */
export function getTranslations(): Translations {
  return currentTranslations
}

/** Lazy-load a locale's translation module */
async function loadLocale(locale: Locale): Promise<Translations> {
  switch (locale) {
    case 'zh-CN': {
      const mod = await import('./locales/zh-CN')
      return mod.default
    }
    default:
      return en
  }
}
